import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ContactSearchSelect from '../events/ContactSearchSelect';
import { contactName } from '../../lib/contactName';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const NO_CONTACTS = [];
const NO_COMPANY_CONTACTS = { accountId: '', rows: [], total: 0, failed: false };
const COMPANY_PAGE = 50;

/**
 * The Contacts field of the deal form (#906): removable chips for the chosen contacts, the contacts at the
 * deal's company listed first, and a search over every contact the user can see.
 *
 * `value` is an array of `{contact_id, first_name, last_name, email}`; `onChange` gets the next array. A
 * `disabled` field keeps its chips, drops the controls and shows `disabledReason`.
 */
export default function ContactMultiSelect({ value, onChange, accountId, accountName, disabled = false, disabledReason }) {
    const { getAuthHeader } = useAuth();
    const selected = value || NO_CONTACTS;
    const [atCompany, setAtCompany] = useState(NO_COMPANY_CONTACTS);

    useEffect(() => {
        if (!accountId || disabled) { setAtCompany(NO_COMPANY_CONTACTS); return undefined; }
        let cancelled = false;
        axios.get(`${API}/contacts`, { params: { account_id: accountId, page_size: COMPANY_PAGE }, headers: getAuthHeader() })
            .then(res => {
                if (cancelled) return;
                const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setAtCompany({ accountId, rows, total: typeof res.data?.total === 'number' ? res.data.total : rows.length, failed: false });
            })
            .catch(() => { if (!cancelled) setAtCompany({ ...NO_COMPANY_CONTACTS, accountId, failed: true }); });
        return () => { cancelled = true; };
    }, [accountId, disabled, getAuthHeader]);

    const selectedIds = useMemo(() => selected.map(c => c.contact_id), [selected]);
    const current = atCompany.accountId === accountId ? atCompany : NO_COMPANY_CONTACTS;
    const companyRows = current.rows.filter(c => !selectedIds.includes(c.contact_id));

    const add = (contact) => {
        if (!contact?.contact_id || selectedIds.includes(contact.contact_id)) return;
        onChange([...selected, { contact_id: contact.contact_id, first_name: contact.first_name || '', last_name: contact.last_name || '', email: contact.email || '' }]);
    };
    const remove = (contactId) => onChange(selected.filter(c => c.contact_id !== contactId));

    return (
        <div className="space-y-2" data-testid="contact-multi-select">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="contact-multi-select-chips">
                    {selected.map(c => (
                        <span key={c.contact_id} className="inline-flex items-center gap-1 rounded-full bg-stone-100 py-0.5 pl-2.5 pr-1.5 text-xs text-stone-700" data-testid={`contact-chip-${c.contact_id}`}>
                            {contactName(c)}
                            {!disabled && (
                                <button type="button" onClick={() => remove(c.contact_id)} aria-label={`Remove ${contactName(c)}`}
                                    className="rounded-full p-0.5 text-stone-400 hover:bg-stone-200 hover:text-stone-700">
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            {disabled ? (
                disabledReason && <p className="text-xs text-stone-500" data-testid="contact-multi-select-reason">{disabledReason}</p>
            ) : (
                <>
                    {companyRows.length > 0 && (
                        <div data-testid="contact-multi-select-company">
                            <p className="mb-1 text-xs font-medium text-stone-500">At {accountName || 'this company'}</p>
                            <div className="max-h-32 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100">
                                {companyRows.map(c => (
                                    <button type="button" key={c.contact_id} onClick={() => add(c)} data-testid={`contact-company-option-${c.contact_id}`}
                                        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-stone-50">
                                        <span className="truncate">{contactName(c)}{c.title && <span className="text-xs text-stone-400"> · {c.title}</span>}</span>
                                        <Plus className="w-3.5 h-3.5 flex-shrink-0 text-stone-400" />
                                    </button>
                                ))}
                            </div>
                            {current.total > current.rows.length && <p className="mt-1 text-xs text-stone-400">Showing the first {current.rows.length}. Search for the others.</p>}
                        </div>
                    )}
                    {current.failed && <p className="text-xs text-stone-400">Contacts at {accountName || 'this company'} could not be loaded. Search for them instead.</p>}
                    <ContactSearchSelect value="" onChange={(contactId, contact) => { if (contactId) add(contact); }}
                        excludeIds={selectedIds} placeholder="Search all contacts by name or email…" emptyText="No matching contacts" />
                </>
            )}
        </div>
    );
}
